import React, { useState, useEffect, useRef } from "react";
import StatCard from "../../components/status-card/StatCard";
import StatTiles from "common-ui-components/components/stat-tiles/StatTiles";
import API from "common-ui-components/components/utils/API";
import styles from "./summary.module.css";
import TableManager from "common-ui-components/components/table-manager";
import Table from "common-ui-components/components/table/Table";
import StatusPill from "common-ui-components/components/pill-cutout/StatusPill";
import { useNavigate } from "react-router-dom";

const SYNC_SUMMARY_URL = "/patch/sync-summary";

const STATUS_LABEL = {
  UP_TO_DATE: "Up to date",
  IN_PROGRESS: "Syncing...",
  SYNCING: "Syncing...",
  IDLE: "Idle",
  QUEUED: "Queued",
  FAILED: "Sync failed",
  NEVER_SYNCED: "Never synced",
};

const RESULT_STATUS_CONFIG = {
  success: { textColor: "#1e8e4e", bgColor: "#e6f7ed" },
  partial: { textColor: "#b98900", bgColor: "#fdf0c6" },
  failed: { textColor: "#d93025", bgColor: "#fde8e4" },
};
const HISTORY_PAGE_SIZE = 5;

const toCamelCase = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  return String(value)
    .trim()
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const TriggerCell = ({ record }) => <span>{toCamelCase(record?.trigger)}</span>;

const ResultStatusPill = ({ record }) => (
  <StatusPill statusConfig={RESULT_STATUS_CONFIG}>
    {toCamelCase(record?.result)}
  </StatusPill>
);

const PatchSyncCount = ({ record }) => {
  const count = Number(record?.applicationPatchSyncedCount ?? 0);
  const width = Math.max(0, Math.min(100, (count / 800) * 100));

  return (
    <div className={styles.patchSyncCount}>
      <span>{count.toLocaleString()}</span>
      <span className={styles.patchSyncTrack}>
        <span
          className={styles.patchSyncBar}
          style={{ width: `${width}%` }}
        />
      </span>
    </div>
  );
};

/**
 * refreshKey: the parent increments this (e.g. after the "Sync now" click)
 * to trigger exactly one new fetch. Leave it at 0 for a plain page visit.
 */
const Summary = ({ refreshKey = 0, compact = false, onSummaryChange }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(compact);
  const [historyError, setHistoryError] = useState(false);
  const [historyRetryKey, setHistoryRetryKey] = useState(0);
  const lastFetchedKeyRef = useRef(null);
  const lastFetchedHistoryKeyRef = useRef(null);

  useEffect(() => {
    const requestKey = `${refreshKey}:${retryKey}`;
    if (lastFetchedKeyRef.current === requestKey) return;
    lastFetchedKeyRef.current = requestKey;

    setLoadError(false);
    API.get(SYNC_SUMMARY_URL)
      .then((response) => {
        if (response?.status?.success !== true || !response?.body) {
          throw new Error(
            response?.status?.message || "Unable to load sync summary.",
          );
        }
        setSummary(response.body);
        onSummaryChange?.(response.body);
      })
      .catch((error) => {
        console.error("Error fetching sync summary:", error);
        setLoadError(true);
        onSummaryChange?.(null);
      });
  }, [refreshKey, retryKey, onSummaryChange]);

  useEffect(() => {
    if (!compact) return;
    const requestKey = `${refreshKey}:${historyRetryKey}`;
    if (lastFetchedHistoryKeyRef.current === requestKey) return;
    lastFetchedHistoryKeyRef.current = requestKey;

    setHistoryLoading(true);
    setHistoryError(false);
    API.post(
      `/patch/sync-history?page=0&size=${HISTORY_PAGE_SIZE}`,
      {
        columns: [],
        sort: {},
        search: [],
        filter: [],
      },
    )
      .then((response) => {
        if (
          response?.status?.success !== true ||
          !Array.isArray(response?.body?.result)
        ) {
          throw new Error(
            response?.status?.message || "Unable to load sync history.",
          );
        }
        setHistory(response.body.result.slice(0, HISTORY_PAGE_SIZE));
        setHistoryTotal(response.body.total ?? response.body.result.length);
      })
      .catch((error) => {
        console.error("Error fetching sync history:", error);
        setHistoryError(true);
      })
      .finally(() => setHistoryLoading(false));
  }, [compact, refreshKey, historyRetryKey]);

  const stats = [
    {
      label: "Last Synced",
      value: summary?.timeSinceLastSync ?? "--",
      valueColor: "#0d9488",
      subtext: summary?.lastSyncedAt ? summary.lastSyncedAt : "",
    },
    {
      label: "Sync Status",
      badge: summary
        ? (STATUS_LABEL[summary.syncStatus] ?? summary.syncStatus)
        : "--",
      badgeVariant: summary?.syncStatus === "FAILED" ? "failed" : undefined,
      subtext: summary?.timeUntilNextSync
        ? `Next auto-sync ${summary.timeUntilNextSync}`
        : "",
    },
    {
      label: "Vulnerabilities Tracked",
      value:
        typeof summary?.vulnerabilitiesTracked === "number"
          ? summary.vulnerabilitiesTracked.toLocaleString()
          : "--",
    },
    {
      label: "Patches Indexed",
      value:
        typeof summary?.patchesIndexed === "number"
          ? summary.patchesIndexed.toLocaleString()
          : "--",
    },
  ];

  const columns = [
    { title: "Date & Time", accessor: "dateTime" },
    {
      title: "Trigger",
      accessor: "trigger",
      WrapperComponent: TriggerCell,
    },
    {
      title: "Result",
      accessor: "result",
      WrapperComponent: ResultStatusPill,
    },
    { title: "Duration (min)", accessor: "durationMin" },
    { title: "MS Patches Synced", accessor: "msPatchSyncedCount" },
    {
      title: "Patches Synced",
      accessor: "applicationPatchSyncedCount",
      // UIComponent: compact ? PatchSyncCount : undefined,
    },
    { title: "Failed Sync Count", accessor: "failedSyncCount" },
  ];

  if (compact) {
    const compactStats = [
      {
        label: "Last synced",
        value: summary?.timeSinceLastSync ?? "--",
        valueColor: "#0d9488",
      },
      {
        label: "Vulnerabilities tracked",
        value:
          typeof summary?.vulnerabilitiesTracked === "number"
            ? summary.vulnerabilitiesTracked.toLocaleString()
            : "--",
      },
      {
        label: "Patches indexed",
        value:
          typeof summary?.patchesIndexed === "number"
            ? summary.patchesIndexed.toLocaleString()
            : "--",
      },
    ];

    return (
      <div className={styles.compactSummary}>
        <div>
          {loadError && (
            <div className={styles.summaryError} role="alert">
              <span>Unable to load patch catalog summary.</span>
              <button
                type="button"
                onClick={() => setRetryKey((key) => key + 1)}
              >
                Retry
              </button>
            </div>
          )}
          <StatTiles items={compactStats} columns={1} className={styles.compactStats} />
        </div>
        <section className={styles.recentHistory}>
          <div className={styles.historyHeading}>
            <h2>Recent Sync History</h2>
            {/* <button
              type="button"
              className={styles.viewAllLink}
              onClick={() => navigate("/app/secure/settings/db-sync")}
            >
              View all {historyTotal} <span aria-hidden="true">→</span>
            </button> */}
          </div>
          {historyError ? (
            <div className={styles.summaryError} role="alert">
              <span>Unable to load recent sync history.</span>
              <button
                type="button"
                onClick={() => setHistoryRetryKey((key) => key + 1)}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className={styles.historyTable}>
              <Table
                columns={columns.filter(
                  (column) => column.accessor !== "failedSyncCount",
                )}
                records={history}
                total={historyLoading ? null : history.length}
                skeletonRowCount={HISTORY_PAGE_SIZE}
                borders={{ borderRadius: "0.75rem", borderColor: "#e2e8f0" }}
                cellPadding="0.8rem"
              />
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {loadError && (
        <div className={styles.summaryError} role="alert">
          <span>Unable to load patch catalog summary.</span>
          <button
            type="button"
            onClick={() => setRetryKey((key) => key + 1)}
          >
            Retry
          </button>
        </div>
      )}
      <div className={styles.row}>
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>
      <section>
        <TableManager
          headingText="Recent Sync History : "
          columns={columns}
          url="/patch/sync-history"
          containerProps={{ style: { borderRadius: "9px" } }}
          exportNeeded={false}
          searchNeeded={false}
        />
      </section>
    </div>
  );
};

export default Summary;
